/* eslint-disable react/jsx-handler-names */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import includes from 'lodash/includes';
import {withRouter} from 'react-router-dom';

import {logCheckpointOnPageChange} from 'js-utils/janus';
import {withEventSaveNotification} from '@eb/destination-utils';
import {setPageLabel, setCustomData} from '@eb/performance-monitor';

import Layout from 'eventbrite_design_system/layout/Layout';

import FilterPanel from './FilterPanel';
import FilterPanelMobile from './FilterPanelMobile';
import ConnectedResultsPanel from '../containers/ConnectedResultsPanel';
import ConnectedMapPanel from '../containers/ConnectedMapPanel';
import ConnectedSearchHeader from '../containers/ConnectedSearchHeader';
import ConnectedSearchFooterLegacy from '../containers/ConnectedSearchFooterLegacy';
import ConnectedSearchFooter from '../containers/ConnectedSearchFooter';
import ConnectedSearchMainContent from '../containers/ConnectedSearchMainContent';

import {
    DESTINATION_SEARCH_EVENT_CLICKED_CHECKPOINT,
    DESTINATION_MANUAL_SEARCH_EVENT_CLICKED_CHECKPOINT,
    NON_MANUAL_SOURCES,
    LUX_LABEL_SEARCH,
    GOOGLE_OPTIMIZE_SEARCH_EXPERIMENT_ID,
} from '../constants';

import './SearchBaseScreen.scss';

const getSearchToDisplay = (userEmail, location, newSearchGoogleOptimize, oldSearch, newSearch, isMounted) => {
    let searchToDisplay = oldSearch;

    if (newSearchGoogleOptimize) {
        searchToDisplay = isMounted ? newSearch : null;
    }

    // For now we want to hide the new UI during SSR to avoid FOUC
    // After new UI is rolled out, we should remove this code and re-run the minimal CSS script.
    if (location.hash === '#new') {
        searchToDisplay = isMounted ? newSearch : null;
    }

    if (userEmail && userEmail.endsWith('eventbrite.com')) {
        searchToDisplay = isMounted ? newSearch : null;
    }

    if (location.hash === '#old') {
        searchToDisplay = oldSearch;
    }

    return searchToDisplay;
};

export class _SearchBaseScreen extends React.PureComponent {
    static propTypes = {
        domain: PropTypes.string.isRequired,
        onSaveEvent: PropTypes.func.isRequired,
        onTrackPageViewAction: PropTypes.func.isRequired,
        updateLocation: PropTypes.func.isRequired,
        updateSearchInformationFromUrl: PropTypes.func.isRequired,
        setSearchInformationFromUrl: PropTypes.func.isRequired,
        isAuthenticated: PropTypes.bool,
        trackEventClickInternal: PropTypes.func,
        triggerPageView: PropTypes.func,
        currentPlace: PropTypes.string,
        currentPlaceParent: PropTypes.string,
        placeType: PropTypes.string,
        selectedFilterCount: PropTypes.number.isRequired,
        resetLink: PropTypes.string.isRequired,
        // eslint-disable-next-line
        history: PropTypes.object,
    }

    constructor(props) {
        super(props);

        this.state = {
            shouldLoadGoogleMap: false,
            hoverEventCardTarget: null,
            isMounted: false,
            newSearchGoogleOptimize: false,
        };
    }

    componentDidMount() {
        const {
            onTrackPageViewAction,
            setSearchInformationFromUrl,
        } = this.props;

        // set the pair location-searchCriterias so we can use them later when
        // the user try to navigate back
        setSearchInformationFromUrl(this.props.history.location.pathname, queryString.parse(this.props.history.location.search));

        // track pageview GA action
        onTrackPageViewAction();
        setPageLabel(LUX_LABEL_SEARCH);
        setCustomData('search_version', 'eb-spa');

        this.setState({isMounted: true});

        if (window.gtag) {
            window.gtag('event', 'optimize.callback', {
                name: GOOGLE_OPTIMIZE_SEARCH_EXPERIMENT_ID,
                callback: (value, name) => this._handleOptimizeCallback(value, name),
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname + prevProps.location.search !== this.props.location.pathname + this.props.location.search) {
            this.props.updateSearchInformationFromUrl(this.props.location.pathname, queryString.parse(this.props.location.search.replace('?', '')));
        }
    }

    _handleOptimizeCallback = (value, name) => {
        if (name === GOOGLE_OPTIMIZE_SEARCH_EXPERIMENT_ID && value === '1') {
            this.setState({newSearchGoogleOptimize: true});
        }
    }

    _handleMouseOverCard = (eid) => {
        this.setState({hoverEventCardTarget: eid});
    }

    _handleMouseOutCard = () => {
        this.setState({hoverEventCardTarget: null});
    }

    _handleGoogleMapsLoaded = () => {
        this.setState({shouldLoadGoogleMap: true});
    }

    _handleOnSaveEvent = (eventId, shouldSave) => {
        const {
            onSaveEvent,
            // location: {
            //     pathname,
            //     query,
            // },
        } = this.props;

        return onSaveEvent(eventId, shouldSave, /*{pathname, query},*/ true);
    }

    _handleTrackEventClickInternal = (data, ticketsBy) => {
        const {
            domain,
            trackEventClickInternal,
        } = this.props;

        const janusCheckpoint = includes(NON_MANUAL_SOURCES, ticketsBy) ? DESTINATION_SEARCH_EVENT_CLICKED_CHECKPOINT : DESTINATION_MANUAL_SEARCH_EVENT_CLICKED_CHECKPOINT;

        logCheckpointOnPageChange(janusCheckpoint, domain);

        trackEventClickInternal(data);
    }

    render() {
        const {
            handleApplyFilter,
            handleRemoveFilter,
            handleLocationSelect,
            isBotRequest,
            userEmail,
            selectedFilterCount,
            resetLink,
            isFilterPanelMobileOpen,
        } = this.props;

        const {hoverEventCardTarget} = this.state;

        const oldSearch = (
            <div className="search-base-screen__old">
                <Layout maxWidth="large">
                    <ConnectedSearchHeader
                        onGoogleMapsLoaded={this._handleGoogleMapsLoaded}
                        history={this.props.history}
                    />
                    <ConnectedSearchMainContent
                        onSaveEvent={this._handleOnSaveEvent}
                        onTrackEventClickInternal={this._handleTrackEventClickInternal}
                        shouldLoadGoogleMap={this.state.shouldLoadGoogleMap}
                        history={this.props.history}
                        hoverEventCardTarget={hoverEventCardTarget}
                        handleMouseOutCard={this._handleMouseOutCard}
                        handleMouseOverCard={this._handleMouseOverCard}
                    />
                    <ConnectedSearchFooterLegacy history={this.props.history} />
                </Layout>
            </div>
        );

        const newSearch = (
            <main
                className={
                    classnames(
                        'search-base-screen__root',
                        {'search-base-screen--is-mobile-filter-open': isFilterPanelMobileOpen}
                    )
                }
            >
                <aside className="search-base-screen__filter-panel">
                    <FilterPanel
                        handleApplyFilter={handleApplyFilter}
                        handleRemoveFilter={handleRemoveFilter}
                        selectedFilterCount={selectedFilterCount}
                        resetLink={resetLink}
                    />
                </aside>
                {
                    isFilterPanelMobileOpen &&
                    <FilterPanelMobile
                        handleApplyFilter={handleApplyFilter}
                        handleRemoveFilter={handleRemoveFilter}
                        selectedFilterCount={selectedFilterCount}
                        resetLink={resetLink}
                    />
                }
                <section className="search-base-screen__search-panel">
                    <ConnectedResultsPanel
                        onSaveEvent={this._handleOnSaveEvent}
                        onTrackEventClickInternal={this._handleTrackEventClickInternal}
                        onGoogleMapsLoaded={this._handleGoogleMapsLoaded}
                        history={this.props.history}
                        isNewSearch={true}
                        handleMouseOutCard={this._handleMouseOutCard}
                        handleMouseOverCard={this._handleMouseOverCard}
                    />
                    {
                        // We want to show the old footer for bots, since it uses links for paginating
                        // instead of buttons
                        isBotRequest ?
                            <ConnectedSearchFooterLegacy history={this.props.history} />
                            :
                            <ConnectedSearchFooter history={this.props.history} />
                    }
                </section>

                <section className="search-base-screen__map-panel">
                    {this.state.shouldLoadGoogleMap &&
                    <ConnectedMapPanel
                        hoverEventCardTarget={hoverEventCardTarget}
                        onSaveEvent={this._handleOnSaveEvent}
                        onTrackEventClickInternal={this._handleTrackEventClickInternal}
                        shouldLoadGoogleMap={this.state.shouldLoadGoogleMap}
                        handleLocationSelect={handleLocationSelect}
                        history={this.props.history}
                    />
                    }
                </section>
            </main>
        );

        const searchToDisplay = getSearchToDisplay(userEmail, this.props.location, this.state.newSearchGoogleOptimize, oldSearch, newSearch, this.state.isMounted);

        return searchToDisplay;
    }
}

export default withRouter(withEventSaveNotification(_SearchBaseScreen));
